import React, { memo, useCallback, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import { Box, Collapse, Drawer, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { NAVIGATION_CONTAINER_TYPES } from 'constants/navigation';
import { IS_SWITCH_THEME_MODE_ALLOWED } from 'constants/themes';

import ThemeToggle from '../../../Header/ThemeToggle';

import {
  childItemIndentStyles,
  drawerPaperStyles,
  expandIconStyles,
  grabberStyles,
  iconWrapperStyles,
  itemButtonStyles,
  sheetHeaderStyles,
  themeRowStyles,
} from './MoreSheet.styles';
import { MoreSheetTestIds } from './MoreSheet.testIds';

const drawerPaperProps = { sx: drawerPaperStyles };

const MoreSheetItem = memo(({ item, onNavigate }) => {
  const location = useLocation();
  const isActive = item.url
    ? !!matchPath(location.pathname, { path: item.url, end: false })
    : false;
  const IconComponent = item.icon;

  const onPress = useCallback(() => {
    onNavigate(item.url);
  }, [item.url, onNavigate]);

  return (
    <Box
      component="button"
      data-testid={MoreSheetTestIds.getItemId(item.id)}
      sx={itemButtonStyles}
      onClick={onPress}
    >
      {IconComponent && (
        <Box sx={iconWrapperStyles}>
          <IconComponent size={IconSize[20]} />
        </Box>
      )}
      <Typography color={isActive ? 'primary.main' : 'text.primary'} variant="regular16">
        {item.title}
      </Typography>
    </Box>
  );
});

MoreSheetItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
    icon: PropTypes.elementType,
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
};

const MoreSheetGroup = memo(({ group, onNavigate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const IconComponent = group.icon;

  const onToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <Box>
      <Box
        component="button"
        data-testid={MoreSheetTestIds.getGroupId(group.id)}
        sx={itemButtonStyles}
        onClick={onToggle}
      >
        {IconComponent && (
          <Box sx={iconWrapperStyles}>
            <IconComponent size={IconSize[20]} />
          </Box>
        )}
        <Typography variant="medium16">{group.title}</Typography>
        <Box sx={expandIconStyles}>
          <ArrowCheckIcon
            direction={isExpanded ? IconDirection.UP : IconDirection.DOWN}
            size={IconSize[20]}
          />
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        {group.children?.map(child => (
          <Box key={child.id} sx={childItemIndentStyles}>
            <MoreSheetItem item={child} onNavigate={onNavigate} />
          </Box>
        ))}
      </Collapse>
    </Box>
  );
});

MoreSheetGroup.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    children: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onNavigate: PropTypes.func.isRequired,
};

/**
 * Bottom sheet showing overflow nav items.
 * ITEMs navigate directly; GROUPs expand to show their children.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Array} props.items - overflow nav items (may include GROUPs and ITEMs)
 */
const MoreSheet = ({ isOpen, onClose, items }) => {
  const history = useHistory();

  const onNavigate = useCallback(
    url => {
      if (url) {
        history.push(url);
        onClose();
      }
    },
    [history, onClose],
  );

  return (
    <Drawer
      PaperProps={drawerPaperProps}
      anchor="bottom"
      data-testid={MoreSheetTestIds.DRAWER}
      open={isOpen}
      onClose={onClose}
    >
      <Box sx={grabberStyles} />
      <Box sx={sheetHeaderStyles}>
        <Typography variant="medium20">More</Typography>
      </Box>
      {items.map(item => {
        const isContainer =
          NAVIGATION_CONTAINER_TYPES.includes(item.type) &&
          Array.isArray(item.children) &&
          item.children.length > 0;

        return isContainer ? (
          <MoreSheetGroup group={item} key={item.id} onNavigate={onNavigate} />
        ) : (
          <MoreSheetItem item={item} key={item.id} onNavigate={onNavigate} />
        );
      })}

      {IS_SWITCH_THEME_MODE_ALLOWED && (
        <Box sx={themeRowStyles}>
          <Typography variant="medium16">Theme</Typography>
          <ThemeToggle onSurface />
        </Box>
      )}
    </Drawer>
  );
};

MoreSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string,
      url: PropTypes.string,
      icon: PropTypes.elementType,
      children: PropTypes.array,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(MoreSheet);
